import type { IStoreRefreshEligibilityOffer } from './useStoreRefresh.types';
import { DISMISSED_AT_PREFIX } from './useStoreRefresh.consts';

export type { IStoreRefreshEligibilityOffer };

export const getDismissedAtMilliseconds = (offerId: string): number | null => {
  const rawValue = localStorage.getItem(DISMISSED_AT_PREFIX + offerId);
  if (rawValue === null) return null;
  const parsedMilliseconds = Number(rawValue);
  return Number.isFinite(parsedMilliseconds) ? parsedMilliseconds : null;
};

export const markDismissed = (offerId: string): void => {
  localStorage.setItem(DISMISSED_AT_PREFIX + offerId, String(Date.now()));
};

export const isOfferOnCooldown = (
  offer: IStoreRefreshEligibilityOffer,
): boolean => {
  const cooldownInMinutes = offer.displayRule?.cooldownInMinutes ?? 0;
  if (cooldownInMinutes <= 0) return false;

  const cooldownInMilliseconds = cooldownInMinutes * 60_000;
  let latestMilliseconds: number | null = null;

  const lastInteractionTime = offer.displayRule?.lastInteractionTime;
  if (lastInteractionTime) {
    const lastInteractionMilliseconds = new Date(lastInteractionTime).getTime();
    if (Number.isFinite(lastInteractionMilliseconds)) {
      latestMilliseconds = lastInteractionMilliseconds;
    }
  }

  const dismissedAtMilliseconds = getDismissedAtMilliseconds(offer.offerId);
  if (
    dismissedAtMilliseconds !== null &&
    (latestMilliseconds === null || dismissedAtMilliseconds > latestMilliseconds)
  ) {
    latestMilliseconds = dismissedAtMilliseconds;
  }

  if (latestMilliseconds === null) return false;
  return Date.now() - latestMilliseconds < cooldownInMilliseconds;
};

export const isOfferEligible = (
  offer: IStoreRefreshEligibilityOffer,
  count: number,
): boolean => {
  const frequency = offer.displayRule?.frequency ?? 1;
  if (count < frequency) return false;
  return !isOfferOnCooldown(offer);
};

export const pickEligibleStoreRefreshOffer = <
  T extends IStoreRefreshEligibilityOffer,
>(
  offers: T[],
  count: number,
): T | undefined => {
  const eligible = offers.filter((offer) => isOfferEligible(offer, count));
  if (eligible.length === 0) return undefined;
  return eligible.sort(
    (offerA, offerB) => (offerA.priority ?? 0) - (offerB.priority ?? 0),
  )[0];
};
